import { Atom } from 'jotai';
import { ROUTES } from '../constants/RouteNameMapping';
import {
  allInvestmentRoundsStateJ,
  investmentRoundSummariesMapStateJ,
} from '../pages/Finance2/state/FinanceStateJ';
import { pageConfigPFJ } from '../pages/PortfolioOverview/state/PageStateJ';
import { viewPFJ, viewsPFJ } from '../pages/PortfolioOverview/state/ViewStateJ';
import { PermissionKey, PersonaKey } from '../services/PermissionAndRolesKeys';
import { PermissionService } from '../services/PermissionService';
import { customFieldsListAtom } from '../services/state/AdminPanel/CustomFieldsStateJ';
import { appConfigRequestAtom, preferencesRequestAtom } from '../services/state/BootStateJ';
import {
  displayedFormulaFieldIdsAtom,
  formulaFieldsListAtom,
} from '../services/state/AdminPanel/FormulaFieldsStateJ';
import { getForesightStore } from './jotai-store';
import { backgroundDeps, isConstrainedConnection, WarmupDeps } from './lazyPage';

type Store = ReturnType<typeof getForesightStore>;

/** What the head starts check: the app's permissions once loaded, the access token's before (early boot). */
export type PrefetchAccess = Pick<PermissionService, 'hasPermission' | 'hasPersona'>;

function appAccess(): PrefetchAccess | null {
  return PermissionService.isInitialized() ? PermissionService.get() : null;
}

// Reading an async atom starts its request; the component that needs it later finds it in flight or
// settled. A failure, thrown or rejected, surfaces where the atom is rendered, so the head start stays
// quiet and can never hold up the boot.
function prime(store: Store, atom: Atom<unknown>) {
  try {
    void Promise.resolve(store.get(atom)).catch(() => undefined);
  } catch {
    // reported by the render that reads the atom
  }
}

// `/` redirects to Portfolio for a metrics user.
function landsOnPortfolio(pathname: string, access: PrefetchAccess): boolean {
  const segment = pathname.split('/')[1] ?? '';
  return (
    (segment === ROUTES.PORTFOLIO || segment === '') && access.hasPermission(PermissionKey.canViewMetrics)
  );
}

// `/portfolio/:viewId` names the view that opens; `/portfolio` alone picks it from the list.
function portfolioViewId(pathname: string): number | null {
  const [, segment, viewId] = pathname.split('/');
  const id = Number(viewId);
  return segment === ROUTES.PORTFOLIO && Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Starts the boot's reads that need no LaunchDarkly flag: configuration, preferences, the round summaries,
 * and the custom fields the shell reads. The early boot calls it as soon as it holds a token.
 */
export function primeBootReads(access: PrefetchAccess, store: Store = getForesightStore()): void {
  try {
    prime(store, appConfigRequestAtom);
    prime(store, preferencesRequestAtom);
    prime(store, investmentRoundSummariesMapStateJ);
    if (!access.hasPersona(PersonaKey.respondent)) {
      prime(store, customFieldsListAtom);
    }
  } catch {
    // a head start only; the boot makes the same requests itself
  }
}

/**
 * Starts the shell's and the landing page's requests during boot instead of after the full-page
 * loader clears. Call once the LD client is in place: fetchViews reads a flag.
 */
export function primeLandingData(
  pathname: string,
  store: Store = getForesightStore(),
  tokenAccess?: PrefetchAccess
): void {
  try {
    const access = tokenAccess ?? appAccess();
    if (!access || access.hasPersona(PersonaKey.respondent)) {
      return;
    }
    // Every page with the shell reads views (section nav, Portfolio link) and custom fields (deal modals).
    prime(store, viewsPFJ);
    prime(store, customFieldsListAtom);
    if (landsOnPortfolio(pathname, access)) {
      prime(store, pageConfigPFJ);
      // The opened view's settings load alongside the list instead of after it.
      const viewId = portfolioViewId(pathname);
      if (viewId != null) {
        prime(store, viewPFJ(viewId));
      }
    }
  } catch {
    // a head start only; the shell makes the same requests itself
  }
}

/** Formula fields sit behind an LD flag; with it on, the grid reads them on its first render. */
export function primeFormulaFields(enabled: boolean, store: Store = getForesightStore()): void {
  if (!enabled) {
    return;
  }
  prime(store, formulaFieldsListAtom);
  prime(store, displayedFormulaFieldIdsAtom);
}

/**
 * Starts, on the first idle slot after the boot, the reads no first screen waits for: the full
 * GET /round the finance pages and the Portfolio grid read. During the boot it would hold the API
 * while the shell's own reads queue behind it; the formatters boot from GET /round/summary instead.
 */
export function primeAfterBoot(
  store: Store = getForesightStore(),
  deps: WarmupDeps = backgroundDeps()
): void {
  try {
    if (
      isConstrainedConnection(deps.connection) ||
      !PermissionService.isInitialized() ||
      PermissionService.get().hasPersona(PersonaKey.respondent)
    ) {
      return;
    }
    deps.idle(() => prime(store, allInvestmentRoundsStateJ));
  } catch {
    // a head start only; the pages that read the rounds request them themselves
  }
}
